import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PriceChart = ({ selectedSymbol, timeframe, onTimeframeChange }) => {
  const [chartType, setChartType] = useState('candlestick');
  const [indicators, setIndicators] = useState({
    sma: false,
    ema: false,
    bollinger: false,
    rsi: false
  });

  // Mock candlestick data
  const mockCandlestickData = useMemo(() => {
    const data = [];
    const basePrice = selectedSymbol === 'BTC/USD' ? 43250 : selectedSymbol === 'ETH/USD' ? 2650 : 0.52;
    const now = new Date();
    
    for (let i = 99; i >= 0; i--) {
      const timestamp = new Date(now.getTime() - i * (timeframe === '1m' ? 60000 : timeframe === '5m' ? 300000 : timeframe === '15m' ? 900000 : timeframe === '1h' ? 3600000 : 86400000));
      const volatility = Math.random() * 0.02 - 0.01;
      const open = basePrice * (1 + volatility);
      const close = open * (1 + (Math.random() * 0.015 - 0.0075));
      const high = Math.max(open, close) * (1 + Math.random() * 0.005);
      const low = Math.min(open, close) * (1 - Math.random() * 0.005);
      
      data?.push({
        time: timestamp?.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          ...(timeframe === '1d' && { month: 'short', day: 'numeric' })
        }),
        timestamp: timestamp?.getTime(),
        open: parseFloat(open?.toFixed(selectedSymbol?.includes('USD') ? 2 : 6)),
        high: parseFloat(high?.toFixed(selectedSymbol?.includes('USD') ? 2 : 6)),
        low: parseFloat(low?.toFixed(selectedSymbol?.includes('USD') ? 2 : 6)),
        close: parseFloat(close?.toFixed(selectedSymbol?.includes('USD') ? 2 : 6)),
        volume: Math.floor(Math.random() * 1000000) + 100000
      });
    }
    return data;
  }, [selectedSymbol, timeframe]);

  const timeframes = [
    { value: '1m', label: '1M' },
    { value: '5m', label: '5M' },
    { value: '15m', label: '15M' },
    { value: '1h', label: '1H' },
    { value: '4h', label: '4H' },
    { value: '1d', label: '1D' }
  ];

  const toggleIndicator = (indicator) => {
    setIndicators(prev => ({
      ...prev,
      [indicator]: !prev?.[indicator]
    }));
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm text-muted-foreground mb-2">{label}</p>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Open:</span>
              <span className="text-foreground font-medium">${data?.open}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">High:</span>
              <span className="text-success font-medium">${data?.high}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Low:</span>
              <span className="text-error font-medium">${data?.low}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Close:</span>
              <span className="text-foreground font-medium">${data?.close}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Volume:</span>
              <span className="text-foreground font-medium">{data?.volume?.toLocaleString()}</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 h-full">
      {/* Chart Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-4">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold text-foreground">{selectedSymbol} Price Chart</h3>
          <div className="flex items-center gap-2">
            <Button
              variant={chartType === 'candlestick' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setChartType('candlestick')}
            >
              <Icon name="BarChart3" size={16} />
              Candlestick
            </Button>
            <Button
              variant={chartType === 'line' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setChartType('line')}
            >
              <Icon name="TrendingUp" size={16} />
              Line
            </Button>
          </div>
        </div>

        {/* Timeframe Selector */}
        <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
          {timeframes?.map((tf) => (
            <Button
              key={tf?.value}
              variant={timeframe === tf?.value ? 'default' : 'ghost'}
              size="sm"
              onClick={() => onTimeframeChange(tf?.value)}
              className="px-3 py-1 text-xs"
            >
              {tf?.label}
            </Button>
          ))}
        </div>
      </div>
      {/* Technical Indicators */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-sm text-muted-foreground">Indicators:</span>
        {Object.entries(indicators)?.map(([key, active]) => (
          <Button
            key={key}
            variant={active ? 'default' : 'outline'}
            size="xs"
            onClick={() => toggleIndicator(key)}
            className="text-xs"
          >
            {key?.toUpperCase()}
          </Button>
        ))}
      </div>
      {/* Chart Container */}
      <div className="h-[400px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'line' ? (
            <LineChart data={mockCandlestickData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="time" 
                stroke="var(--color-muted-foreground)"
                fontSize={12}
              />
              <YAxis 
                stroke="var(--color-muted-foreground)"
                fontSize={12}
                domain={['dataMin - 10', 'dataMax + 10']}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line 
                type="monotone" 
                dataKey="close" 
                stroke="var(--color-primary)" 
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          ) : (
            <LineChart data={mockCandlestickData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="time" 
                stroke="var(--color-muted-foreground)"
                fontSize={12}
              />
              <YAxis 
                stroke="var(--color-muted-foreground)"
                fontSize={12}
                domain={['dataMin - 10', 'dataMax + 10']}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line 
                type="monotone" 
                dataKey="high" 
                stroke="var(--color-success)" 
                strokeWidth={1}
                dot={false}
              />
              <Line 
                type="monotone" 
                dataKey="low" 
                stroke="var(--color-error)" 
                strokeWidth={1}
                dot={false}
              />
              <Line 
                type="monotone" 
                dataKey="close" 
                stroke="var(--color-primary)" 
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>
      {/* Chart Controls */}
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Icon name="ZoomIn" size={16} />
            Zoom
          </Button>
          <Button variant="outline" size="sm">
            <Icon name="Move" size={16} />
            Pan
          </Button>
          <Button variant="outline" size="sm">
            <Icon name="RotateCcw" size={16} />
            Reset
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Icon name="Download" size={16} />
            Export
          </Button>
          <Button variant="outline" size="sm">
            <Icon name="Share" size={16} />
            Share
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PriceChart;